import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { MediaRegistry } from '../../registries/MediaRegistry';
import { Tool } from '../Tool';

import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import WebSocketClient from 'js/src/libs/websocket-client';
import { isErrorObject } from 'js/src/types';

export const sketchUrlFetchSchema = z.object({
    url: z.string().describe('The URL to fetch content from'),
});
export type SketchUrlFetchArgs = z.infer<typeof sketchUrlFetchSchema>;

interface SketchUrlFetchResponse {
    url: string;
    content: string;
    status: 'success' | 'error';
    media_asset_uuids?: string[];
}

interface UrlFetchToolResult {
    content: string;
    mediaIds: string[];
}

export class SketchUrlFetchTool extends Tool<typeof sketchUrlFetchSchema> {
    protected override readonly schema = sketchUrlFetchSchema;
    protected override readonly toolName = TOOL_NAMES.fetchUrlContent;
    protected override readonly toolDescription =
        'Fetches and parses text content and images from an HTML URL provided by the user. ONLY call this tool when the user\'s message contains an actual URL string. DO NOT call this tool if the user only mentions URLs generically without providing them (e.g., "from this URL", "a link I found", "some website") - in those cases, ask the user to provide the actual URL. This tool only works with HTML/text URLs, not PDFs, images, or videos.';

    public override canRunAsync = true;

    public override getToolDisplayName(): string {
        return 'Fetching URL content';
    }

    public get statusIcon(): string {
        return 'language';
    }

    public async execute(args: SketchUrlFetchArgs) {
        const { url } = args;
        if (!url) {
            return 'Error: No URL provided. Please provide a URL to fetch content from.';
        }

        try {
            const response = await ApiService.sketchUrlFetchCreate({ body: { url } });

            const ws = WebSocketClient.getInstance();
            const result = await ws.waitForMessage<SketchUrlFetchResponse>(
                response.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'sketch_url_fetch',
            );

            if (result.status === 'error') {
                return `Failed to fetch content from ${url}: ${result.content}`;
            }

            // Add extracted media assets to the MediaRegistry
            const mediaRegistry = MediaRegistry.getInstance();
            let mediaRegistryIds: string[] = [];

            if (result.media_asset_uuids && result.media_asset_uuids.length > 0) {
                const ids = await mediaRegistry.addItemsByUuids(result.media_asset_uuids);
                mediaRegistryIds = ids.filter((id): id is string => id !== null);
            }

            const toolResult: UrlFetchToolResult = {
                content: result.content,
                mediaIds: mediaRegistryIds,
            };
            return JSON.stringify(toolResult);
        } catch (error) {
            const errorMessage = isErrorObject(error)
                ? error.error.message
                : error instanceof Error
                  ? error.message
                  : 'Unknown error occurred';
            return `Error fetching URL content: ${errorMessage}`;
        }
    }
}
